// HAND-BUILT REALISTIC HULLS for the two most-parked rides (2026-11-05, owner:
// "continue … the 3D model replacement"). The SCRAP KART tank (`kart`) and the
// HIND GUNSHIP (`heli`) each sit on 13 maps, and both were a handful of
// rounded slabs. They are now built here, procedurally, from our own
// geometry — no downloaded mesh, so no third-party credit:
//   tank    — a real track BELT (hollow extruded loop) around 5 rubber-rimmed
//             road wheels + sprocket + idler + return rollers, fenders, side
//             skirts, sloped glacis and nose plate, engine-deck grilles and
//             exhausts, a faceted cast turret with tapered cheeks, mantlet,
//             fume-extractor gun, cupola, hatches, MG, smoke dischargers,
//             bustle rack and antenna;
//   gunship — a lofted streamlined fuselage that tapers into the tail boom,
//             stepped tandem canopies, engine deck with intakes/exhausts, stub
//             wings with rocket pods and rails, chin gun, wheeled gear, swept
//             fin, a 5-blade main rotor and a spinning 3-blade tail rotor.
// THE GAME'S LOOK IS KEPT: the same cfg.color / cfg.accent paint, the same
// rounded-panel family and the same vehicleMaps surfaces. The hull paint is
// still `bodyMat` (passed in), so a driver's colour and custom-drawn SKIN land
// exactly as before via VehicleMesh.setDriver, and the accent parts still
// take the driver's glow colour. No new material kinds are introduced, so no
// new shader variants (bench-warmcov).
//
// Everything here runs once at construction; VehicleMesh.mergeVehicleBody
// collapses the hull, and the caller merges the turret / rotor groups, so the
// draw-call count stays per-material. Visual only: colliders live in shared
// (VEHICLES[].half) and are untouched.
import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { worldProjectUV } from "./mapTextures.js";
import { roundCrossSection } from "./avatar.js";

export interface HullWheelSpot { x: number; y: number; z: number; r: number; w: number; spokes?: number }

export interface HullMats {
  /** driver-tinted / skinned hull paint (VehicleMesh.bodyMat) */
  body: THREE.Material;
  /** driver-glow-tinted accent (VehicleMesh.accentMat) */
  accent: THREE.Material;
  dark: THREE.Material;
  glass?: THREE.Material;
  track?: THREE.Material;
}

export interface HullParts {
  turret?: THREE.Group;
  rotor?: THREE.Group;
  tailRotor?: THREE.Group;
}

type V3 = [number, number, number];

function rbox(w: number, h: number, d: number, mat: THREE.Material, p: V3, tile = 0.9): THREE.Mesh {
  // thin trim (rails, blades, grilles) gets one bevel segment: its chamfer is
  // sub-centimetre, so the second segment was pure triangle cost
  const geo = new RoundedBoxGeometry(w, h, d, Math.min(w, h, d) < 0.2 ? 1 : 2, Math.min(0.08, Math.min(w, h, d) * 0.25));
  worldProjectUV(geo, tile);
  const m = new THREE.Mesh(geo, mat);
  m.position.set(p[0], p[1], p[2]);
  m.castShadow = true;
  return m;
}

function rhull(w: number, h: number, d: number, mat: THREE.Material, p: V3, round = 0.85, tile = 0.9): THREE.Mesh {
  const geo = new RoundedBoxGeometry(w, h, d, 3, Math.min(0.1, Math.min(w, h, d) * 0.25));
  roundCrossSection(geo, w, d, round);
  worldProjectUV(geo, tile);
  const m = new THREE.Mesh(geo, mat);
  m.position.set(p[0], p[1], p[2]);
  m.castShadow = true;
  return m;
}

function cyl(r1: number, r2: number, h: number, mat: THREE.Material, p: V3, axis: "x" | "y" | "z" = "y", seg = 14, tile = 0.4): THREE.Mesh {
  const geo = new THREE.CylinderGeometry(r1, r2, h, seg);
  if (axis === "z") geo.rotateX(-Math.PI / 2); // +y end → −z (forward)
  else if (axis === "x") geo.rotateZ(-Math.PI / 2);
  worldProjectUV(geo, tile);
  const m = new THREE.Mesh(geo, mat);
  m.position.set(p[0], p[1], p[2]);
  m.castShadow = true;
  return m;
}

/** A lofted body: superellipse cross-sections [z, halfW, halfH, centreY]
 *  joined into one smooth skin (fuselages that taper into a boom). */
function loft(stations: number[][], mat: THREE.Material, seg = 20, n = 2.6, tile = 1.0): THREE.Mesh {
  const pos: number[] = [];
  const idx: number[] = [];
  const e = 2 / n;
  for (const [z, hw, hh, cy] of stations) {
    for (let i = 0; i < seg; i++) {
      const a = (i / seg) * Math.PI * 2;
      const c = Math.cos(a), s = Math.sin(a);
      pos.push(hw * Math.sign(c) * Math.abs(c) ** e, cy + hh * Math.sign(s) * Math.abs(s) ** e, z);
    }
  }
  for (let r = 0; r < stations.length - 1; r++) {
    for (let i = 0; i < seg; i++) {
      const a = r * seg + i, b = r * seg + ((i + 1) % seg);
      const c = a + seg, d = b + seg;
      idx.push(a, b, c, b, d, c); // outward-facing (CCW rings marching +z)
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(new Float32Array((pos.length / 3) * 2), 2));
  geo.setIndex(idx);
  geo.computeVertexNormals();
  worldProjectUV(geo, tile);
  const m = new THREE.Mesh(geo, mat);
  m.castShadow = true;
  return m;
}

// ---------------------------------------------------------------- TANK ----

/** Track BELT side profile (u = world z, v = world y) — outer loop and the
 *  inner hole 0.09 m in, so the road wheels show through the open sides. */
function trackShape(): THREE.Shape {
  const L = 1.82, B = 1.3, T = 0.8, t = 0.09;
  const s = new THREE.Shape();
  s.moveTo(-B, 0);
  s.lineTo(B, 0);
  s.quadraticCurveTo(L, 0, L, 0.4);
  s.quadraticCurveTo(L, T - 0.02, L - 0.36, T);
  s.lineTo(-L + 0.32, T);
  s.quadraticCurveTo(-L - 0.02, T, -L, 0.5);
  s.quadraticCurveTo(-L + 0.04, 0.05, -B, 0);
  const h = new THREE.Path();
  h.moveTo(-B + 0.02, t);
  h.quadraticCurveTo(-L + 0.04 + t, 0.05 + t, -L + t, 0.5);
  h.quadraticCurveTo(-L - 0.02 + t, T - t, -L + 0.32, T - t);
  h.lineTo(L - 0.36, T - t);
  h.quadraticCurveTo(L - t, T - 0.02 - t, L - t, 0.4);
  h.quadraticCurveTo(L - t, t, B - 0.02, t);
  h.lineTo(-B + 0.02, t);
  s.holes.push(h);
  return s;
}

function trackBelt(x: number, width: number, mat: THREE.Material): THREE.Mesh {
  const geo = new THREE.ExtrudeGeometry(trackShape(), { depth: width, bevelEnabled: false, curveSegments: 6 });
  geo.rotateY(-Math.PI / 2); // shape x → world z, extrusion → world −x
  geo.translate(x + width / 2, 0, 0);
  worldProjectUV(geo, 0.55);
  const m = new THREE.Mesh(geo, mat);
  m.castShadow = true;
  return m;
}

/** Faceted cast turret: plan-view polygon extruded up, cheeks tapered in. */
function turretShell(mat: THREE.Material): THREE.Mesh {
  const plan: [number, number][] = [
    [-0.32, -1.0], [0.32, -1.0], [0.82, -0.5], [0.86, 0.7], [0.62, 1.1], [-0.62, 1.1], [-0.86, 0.7], [-0.82, -0.5],
  ];
  const s = new THREE.Shape();
  plan.forEach(([x, z], i) => (i ? s.lineTo(x, -z) : s.moveTo(x, -z)));
  const H = 0.46;
  const geo = new THREE.ExtrudeGeometry(s, { depth: H, bevelEnabled: true, bevelThickness: 0.05, bevelSize: 0.05, bevelSegments: 2, curveSegments: 1 });
  geo.rotateX(-Math.PI / 2); // extrusion → +y, shape y → −z
  const p = geo.attributes.position;
  for (let i = 0; i < p.count; i++) {
    const y = p.getY(i);
    const k = 1 - 0.13 * Math.max(0, Math.min(1, y / (H + 0.05)));
    p.setXYZ(i, p.getX(i) * k, y + 0.05, p.getZ(i) * k);
  }
  geo.computeVertexNormals();
  worldProjectUV(geo, 1.0);
  const m = new THREE.Mesh(geo, mat);
  m.castShadow = true;
  return m;
}

/** The SCRAP KART tank, in the pre-scale frame the old build used (the
 *  caller scales the group 0.82). Forward −z, ground y 0. */
export function buildTankHull(
  group: THREE.Group, mats: HullMats, wheels: (spots: HullWheelSpot[]) => void,
): HullParts {
  const { body: armour, accent, dark } = mats;
  const track = mats.track ?? dark;
  const TX = 1.1, TW = 0.55;
  // TRACK BELTS + running gear
  for (const sx of [-1, 1]) group.add(trackBelt(sx * TX, TW, track));
  const spots: HullWheelSpot[] = [];
  for (const sx of [-1, 1]) {
    for (let i = 0; i < 5; i++) spots.push({ x: sx * TX, y: 0.37, z: -1.1 + i * 0.55, r: 0.28, w: 0.42, spokes: 6 });
    spots.push({ x: sx * TX, y: 0.5, z: -1.5, r: 0.25, w: 0.4, spokes: 8 }); // drive sprocket
    spots.push({ x: sx * TX, y: 0.42, z: 1.48, r: 0.26, w: 0.4, spokes: 6 }); // idler
  }
  wheels(spots);
  for (const sx of [-1, 1]) for (const z of [-0.6, 0, 0.6]) group.add(cyl(0.08, 0.08, 0.36, dark, [sx * TX, 0.63, z], "x", 10)); // return rollers
  // HULL
  group.add(rbox(1.66, 0.55, 3.3, armour, [0, 0.58, 0], 1.0)); // lower hull between the tracks
  group.add(rbox(2.44, 0.1, 3.5, armour, [0, 0.9, 0.04], 1.0)); // fender deck over the tracks
  group.add(rbox(1.92, 0.34, 2.6, armour, [0, 1.1, 0.28], 1.0)); // upper hull / engine deck
  const glacis = rbox(1.92, 0.3, 1.0, armour, [0, 0.98, -1.24], 1.0);
  glacis.rotation.x = -0.32; group.add(glacis); // sloped glacis
  const nose = rbox(1.66, 0.42, 0.26, armour, [0, 0.55, -1.66], 1.0);
  nose.rotation.x = 0.55; group.add(nose); // lower nose plate
  group.add(rbox(1.92, 0.5, 0.16, armour, [0, 0.82, 1.72], 1.0)); // rear plate
  for (const sx of [-1, 1]) {
    group.add(rbox(0.07, 0.22, 3.3, armour, [sx * 1.23, 0.82, 0.02], 0.8)); // side skirt
    group.add(rbox(0.46, 0.12, 0.08, accent, [sx * 0.98, 0.9, -1.78], 0.5)); // fender-tip hazard bar
    group.add(rbox(0.34, 0.2, 0.86, armour, [sx * 0.98, 1.05, 0.72], 0.8)); // fender stowage box
    group.add(rbox(0.16, 0.12, 0.08, dark, [sx * 0.6, 1.02, -1.74], 0.4)); // headlight guard
    group.add(cyl(0.05, 0.05, 0.12, dark, [sx * 0.6, 1.02, -1.8], "z", 10)); // headlight
    group.add(rbox(0.12, 0.12, 0.16, dark, [sx * 0.5, 0.42, -1.82], 0.4)); // tow hook
    group.add(cyl(0.08, 0.09, 0.32, dark, [sx * 0.62, 0.98, 1.86], "z", 12)); // exhaust
  }
  for (let i = 0; i < 5; i++) group.add(rbox(1.36, 0.035, 0.07, dark, [0, 1.285, 0.92 + i * 0.12], 0.4)); // deck grille
  group.add(rbox(1.2, 0.05, 0.22, track, [0, 1.21, -0.9], 0.5)); // spare track links on the glacis edge

  // TURRET on its own yaw pivot (VehicleMesh.setTurretYaw)
  const turret = new THREE.Group();
  turret.position.set(0, 1.26, 0);
  turret.add(turretShell(armour));
  turret.add(rbox(0.62, 0.38, 0.32, armour, [0, 0.27, -1.1], 0.6)); // mantlet
  turret.add(cyl(0.085, 0.1, 2.1, dark, [0, 0.27, -2.2], "z", 14)); // gun tube
  turret.add(cyl(0.14, 0.14, 0.42, dark, [0, 0.27, -2.3], "z", 14)); // fume extractor
  turret.add(cyl(0.12, 0.12, 0.18, dark, [0, 0.27, -3.22], "z", 14)); // muzzle
  turret.add(cyl(0.26, 0.29, 0.2, armour, [0.36, 0.6, 0.42], "y", 14)); // commander's cupola
  turret.add(cyl(0.22, 0.22, 0.05, dark, [0.36, 0.72, 0.42], "y", 14)); // cupola hatch
  for (const a of [-0.9, 0, 0.9]) turret.add(rbox(0.1, 0.07, 0.06, dark, [0.36 + Math.sin(a) * 0.27, 0.62, 0.42 - Math.cos(a) * 0.27], 0.3)); // vision blocks
  turret.add(cyl(0.22, 0.22, 0.05, armour, [-0.36, 0.54, 0.32], "y", 14)); // loader's hatch
  turret.add(rbox(0.08, 0.16, 0.08, dark, [-0.36, 0.64, -0.12], 0.3)); // MG mount
  turret.add(cyl(0.03, 0.03, 0.8, dark, [-0.36, 0.72, -0.4], "z", 8)); // MG barrel
  for (const sx of [-1, 1]) {
    for (let i = 0; i < 3; i++) turret.add(cyl(0.05, 0.05, 0.22, dark, [sx * (0.62 + i * 0.07), 0.38 + i * 0.03, -0.66], "z", 8)); // smoke dischargers
  }
  turret.add(cyl(0.015, 0.025, 1.3, dark, [0.58, 1.1, 0.98], "y", 6)); // whip antenna
  for (const y of [0.22, 0.42]) turret.add(rbox(1.3, 0.05, 0.05, dark, [0, y, 1.3], 0.4)); // bustle rack rails
  for (const x of [-0.6, 0, 0.6]) turret.add(rbox(0.05, 0.3, 0.05, dark, [x, 0.32, 1.3], 0.4)); // rack posts
  group.add(turret);
  return { turret };
}

// ------------------------------------------------------------- GUNSHIP ----

/** The HIND GUNSHIP. Forward −z, ground y 0 (wheels). */
export function buildGunshipHull(
  group: THREE.Group, mats: HullMats, wheels: (spots: HullWheelSpot[]) => void,
): HullParts {
  const { body, accent, dark } = mats;
  const glass = mats.glass ?? dark;
  // FUSELAGE: one lofted skin from the nose through the cabin into the boom
  group.add(loft([
    [-2.6, 0.04, 0.04, 0.95],
    [-2.5, 0.28, 0.3, 0.98],
    [-2.2, 0.45, 0.48, 1.05],
    [-1.6, 0.62, 0.62, 1.12],
    [-0.8, 0.78, 0.72, 1.2],
    [0.2, 0.86, 0.75, 1.25],
    [1.1, 0.8, 0.68, 1.3],
    [1.7, 0.52, 0.48, 1.45],
    [2.4, 0.3, 0.3, 1.66],
    [3.6, 0.2, 0.2, 1.85],
    [4.25, 0.17, 0.17, 1.92],
    [4.4, 0.03, 0.03, 1.93],
  ], body));
  // stepped tandem canopies (gunner low in front, pilot raised behind)
  group.add(rhull(0.7, 0.44, 0.95, glass, [0, 1.6, -1.72], 1, 0.9));
  group.add(rhull(0.82, 0.56, 1.05, glass, [0, 1.92, -0.72], 1, 0.9));
  group.add(rbox(0.74, 0.07, 0.07, dark, [0, 1.84, -1.24], 0.3)); // canopy frame
  group.add(rbox(0.07, 0.07, 1.0, dark, [0, 2.2, -0.72], 0.3));   // canopy spine
  // ENGINE DECK + intakes + exhausts
  group.add(rhull(1.3, 0.5, 2.4, body, [0, 1.98, 0.35], 0.8, 1.0));
  for (const sx of [-1, 1]) {
    group.add(cyl(0.2, 0.22, 0.3, dark, [sx * 0.42, 2.0, -0.9], "z", 14)); // intake
    group.add(cyl(0.16, 0.2, 0.5, dark, [sx * 0.6, 2.02, 1.5], "z", 12));  // exhaust
  }
  group.add(cyl(0.12, 0.16, 0.5, dark, [0, 2.45, 0.2], "y", 12)); // mast
  group.add(cyl(0.28, 0.32, 0.18, accent, [0, 2.72, 0.2], "y", 14)); // rotor head
  // STUB WINGS with rocket pods + wingtip rails
  for (const sx of [-1, 1]) {
    const wing = rbox(1.5, 0.09, 0.75, body, [sx * 1.45, 1.3, 0.25], 0.9);
    wing.rotation.z = -sx * 0.12; group.add(wing); // anhedral
    for (const ax of [1.15, 1.8]) {
      const wy = 1.3 - (ax - 1.45) * 0.12;
      group.add(rbox(0.06, 0.16, 0.4, dark, [sx * ax, wy - 0.12, 0.25], 0.3)); // pylon
      group.add(cyl(0.17, 0.17, 0.95, dark, [sx * ax, wy - 0.34, 0.2], "z", 14)); // rocket pod
      group.add(cyl(0.18, 0.12, 0.12, accent, [sx * ax, wy - 0.34, -0.32], "z", 14)); // pod nose ring
    }
    group.add(rbox(0.1, 0.24, 0.9, dark, [sx * 2.22, 1.13, 0.25], 0.3)); // wingtip rail
  }
  // CHIN GUN
  group.add(rhull(0.42, 0.36, 0.42, dark, [0, 0.66, -2.05], 1, 0.4));
  group.add(cyl(0.04, 0.04, 0.7, dark, [0, 0.6, -2.45], "z", 8));
  // WHEELED GEAR (nose + two mains) on struts
  wheels([
    { x: 0, y: 0.2, z: -1.9, r: 0.2, w: 0.14, spokes: 5 },
    { x: -0.95, y: 0.26, z: 0.35, r: 0.26, w: 0.2, spokes: 5 },
    { x: 0.95, y: 0.26, z: 0.35, r: 0.26, w: 0.2, spokes: 5 },
  ]);
  group.add(cyl(0.05, 0.05, 0.44, dark, [0, 0.44, -1.9], "y", 8));
  for (const sx of [-1, 1]) {
    const strut = cyl(0.06, 0.06, 0.66, dark, [sx * 0.78, 0.52, 0.35], "y", 8);
    strut.rotation.z = sx * 0.62; group.add(strut);
  }
  // TAIL: swept fin + stabiliser (accent, like the old build)
  const fin = rbox(0.1, 1.1, 0.75, accent, [0, 2.32, 4.08], 0.7);
  fin.rotation.x = 0.35; group.add(fin);
  group.add(rbox(1.3, 0.08, 0.4, accent, [0, 1.9, 3.5], 0.7));

  // MAIN ROTOR: 5 blades about a hub (spins about +y)
  const rotor = new THREE.Group();
  rotor.position.set(0, 2.8, 0.2);
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    const b = rbox(4.3, 0.06, 0.34, dark, [Math.cos(a) * 2.45, 0, -Math.sin(a) * 2.45], 0.9);
    b.rotation.y = a; rotor.add(b);
  }
  rotor.add(cyl(0.22, 0.22, 0.12, dark, [0, 0.02, 0], "y", 12));
  group.add(rotor);
  // TAIL ROTOR: 3 blades in the YZ plane (spins about +x)
  const tailRotor = new THREE.Group();
  tailRotor.position.set(0.16, 2.45, 4.22);
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    const b = rbox(0.05, 0.75, 0.12, dark, [0, Math.cos(a) * 0.38, Math.sin(a) * 0.38], 0.4);
    b.rotation.x = a; tailRotor.add(b);
  }
  tailRotor.add(cyl(0.07, 0.07, 0.12, dark, [0, 0, 0], "x", 10));
  group.add(tailRotor);
  return { rotor, tailRotor };
}
